import {
  AgentCommandPendingResponseSchema,
  type CancelBackgroundTaskControl,
} from '@reflex/shared';
import { toast } from '@reflex/ui/lib/toast';
import { api } from '@/lib/api';
import { deriveBackgroundTasks } from '@/components/stream/background-tasks';
import { useCommandOutcomeWatch, type CommandOutcome } from './useCommandOutcomeWatch';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AgentActivityBar,
  type AgentActivityDaemon,
} from '@reflex/ui/components/agent/AgentActivityBar';
import type { AgentThreadListEntry } from '@reflex/ui/components/agent/AgentThreadList';
import { daemonLiveState, matchDaemonService } from '@/components/agent/agent-server-status';
import { ServiceLogSheet, type ServiceLogTarget } from '@/components/agent/ServiceLogSheet';
import { deriveSubagentEntries } from '@/components/stream/subagent-activity';
import { useServiceStatuses } from '@/hooks/agents/useServiceStatuses';
import {
  useAgentListeners,
  useCancelAgentListener,
  useDecideCapabilityRequest,
  useRenewAgentListener,
} from '@/hooks/agents/useAgentListeners';
import { hasCodexInjectionCapability } from '@reflex/shared';
import { useOrganization } from '@/hooks/auth/useOrganization';
import { listenerRow } from '@/components/agent/agent-listener-rows';
import type { Agent, AxonEvent } from '@/lib/types';

interface AgentActivityDockProps {
  agent: Pick<Agent, 'id' | 'daemons' | 'devboxId'> &
    Partial<Pick<Agent, 'agentType' | 'status' | 'turnState' | 'backgroundTasks'>>;
  canCancelBackgroundTasks?: boolean;
  isTurnWorking?: boolean;
  events: AxonEvent[];
  /**
   * Jump to a sub-agent launch. Stream entries carry the `agent.subagent`
   * started event's id, so wiring this to the transcript's highlight path
   * scrolls to and flashes the launch; injected `launchedSubagents` entries
   * carry the child agent's id instead, so the host can route those to the
   * agent itself.
   */
  onOpenSubagent?: (id: string) => void;
  /**
   * Agents this thread launched through Reflex MCP (the chat view parses and
   * emits them), listed ahead of the stream-derived launches. Entries whose
   * id collides with a stream entry win over it.
   */
  launchedSubagents?: AgentThreadListEntry[];
  className?: string;
}

/**
 * The composer's activity rail for a live agent thread: folds the thread's
 * sub-agent launches (from `agent.subagent` stream events plus any injected
 * `launchedSubagents`) and the daemons registered on the sandbox
 * (`agent.daemons`) and the listeners it armed (`/agents/:id/listeners`, behind
 * the org's `codex-injection` capability) into one `AgentActivityBar` line docked
 * above the input.
 * This is the home of the daemons surface on the stream view: expanding
 * probes the on-box process state so each daemon's dot reflects pid
 * liveness, and matched daemons open the same `ServiceLogSheet` the detail
 * panel uses. Renders nothing until the thread has launches or daemons.
 */
export function AgentActivityDock({
  agent,
  events,
  canCancelBackgroundTasks = false,
  isTurnWorking = agent.turnState === 'working',
  onOpenSubagent,
  launchedSubagents,
  className,
}: AgentActivityDockProps) {
  const backgroundTasks = useMemo(
    () =>
      agent.agentType === 'codex' && agent.status === 'running' && !isTurnWorking
        ? deriveBackgroundTasks(events, agent.backgroundTasks ?? [])
        : [],
    [agent.agentType, agent.status, agent.backgroundTasks, isTurnWorking, events],
  );
  const [cancellations, setCancellations] = useState<
    { clientId: string; createdAt: number; commandId?: string; status: 'sending' }[]
  >([]);
  const activeCancellations = useMemo(() => {
    const activeIds = new Set(backgroundTasks.map((task) => task.id));
    return cancellations.filter((entry) => activeIds.has(entry.clientId));
  }, [backgroundTasks, cancellations]);
  const finishCancellation = useCallback((id: string, outcome?: CommandOutcome) => {
    setCancellations((pending) => pending.filter((entry) => entry.clientId !== id));
    if (outcome?.status === 'failed')
      toast.error('Could not cancel background task', {
        description: outcome.failure.detail ?? outcome.failure.code,
      });
  }, []);
  useCommandOutcomeWatch(
    activeCancellations,
    agent.id,
    api.getAgentCommandStatus,
    finishCancellation,
    60_000,
  );
  useEffect(() => {
    if (!activeCancellations.length) return;
    const timer = setTimeout(() => {
      toast.error('Cancellation has not been confirmed. Try again.');
      setCancellations([]);
    }, 60_000);
    return () => clearTimeout(timer);
  }, [activeCancellations]);
  async function cancelTask(taskId: string) {
    setCancellations((pending) => [
      ...pending.filter(
        (entry) =>
          entry.clientId !== taskId && backgroundTasks.some((task) => task.id === entry.clientId),
      ),
      { clientId: taskId, createdAt: Date.now(), status: 'sending' },
    ]);
    try {
      const payload: CancelBackgroundTaskControl = { action: 'cancel_background_task', taskId };
      const result = await api.sendControlResponse(agent.id, payload);
      const pending = AgentCommandPendingResponseSchema.safeParse(result);
      if (pending.success) {
        setCancellations((entries) =>
          entries.map((entry) =>
            entry.clientId === taskId ? { ...entry, commandId: pending.data.commandId } : entry,
          ),
        );
      } else finishCancellation(taskId);
    } catch (error) {
      finishCancellation(taskId);
      toast.error('Could not cancel background task', {
        description: error instanceof Error ? error.message : undefined,
      });
    }
  }
  const [probing, setProbing] = useState(false);
  const [logTarget, setLogTarget] = useState<ServiceLogTarget | null>(null);
  // Launched agents lead the list; the bar's own attention ordering still
  // lifts whichever entries need the user (blocked, then working) to the top.
  // The id filter is defensive only: launched entries carry agent ids and
  // derived entries carry stream event ids, which never collide today.
  const subagents = useMemo(() => {
    const derived = deriveSubagentEntries(events);
    if (!launchedSubagents?.length) return derived;
    const launchedIds = new Set(launchedSubagents.map((entry) => entry.id));
    return [...launchedSubagents, ...derived.filter((entry) => !launchedIds.has(entry.id))];
  }, [events, launchedSubagents]);
  // Probe only while the panel is open; the collapsed line stays passive.
  const { data: statuses } = useServiceStatuses(agent.id, {
    enabled: probing && Boolean(agent.devboxId) && (agent.daemons?.length ?? 0) > 0,
  });

  // Discovery and delivery share the org capability; updates arrive over WS.
  const { activeOrganization } = useOrganization();
  const listenersEnabled = hasCodexInjectionCapability(activeOrganization?.capabilities);
  const { data: listenerRows } = useAgentListeners(agent.id, { enabled: listenersEnabled });
  const decide = useDecideCapabilityRequest(agent.id);
  const cancelListener = useCancelAgentListener(agent.id);
  const renewListener = useRenewAgentListener(agent.id);
  const listeners = useMemo(
    () => (listenersEnabled ? (listenerRows ?? []).map((listener) => listenerRow(listener)) : []),
    [listenerRows, listenersEnabled],
  );

  const services = statuses?.services;
  const daemons = useMemo<(AgentActivityDaemon & { serviceId?: string })[]>(
    () =>
      (agent.daemons ?? []).map((daemon) => {
        const service = matchDaemonService(daemon, services);
        return {
          ...daemon,
          state: daemonLiveState(service, services !== undefined),
          hasLogs: service != null,
          serviceId: service?.id,
        };
      }),
    [agent.daemons, services],
  );

  return (
    <>
      <AgentActivityBar
        subagents={subagents}
        daemons={daemons}
        backgroundTasks={backgroundTasks}
        onCancelBackgroundTask={
          canCancelBackgroundTasks && agent.agentType === 'codex'
            ? (id) => void cancelTask(id)
            : undefined
        }
        cancellingTaskIds={new Set(cancellations.map((entry) => entry.clientId))}
        listeners={listeners}
        onDecideListener={(requestId, answer) => decide.mutate({ requestId, answer })}
        onCancelListener={(listenerId) => cancelListener.mutate({ listenerId })}
        onRenewListener={(listenerId) => renewListener.mutate({ listenerId })}
        onOpenSubagent={onOpenSubagent}
        onOpenDaemonLogs={(index) => {
          const daemon = daemons[index];
          if (daemon?.serviceId) {
            setLogTarget({ serviceId: daemon.serviceId, name: daemon.name });
          }
        }}
        onExpandedChange={setProbing}
        className={className}
      />
      <ServiceLogSheet
        agentId={agent.id}
        target={logTarget}
        onOpenChange={(isOpen) => {
          if (!isOpen) setLogTarget(null);
        }}
      />
    </>
  );
}
